"use client";

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import { X } from "lucide-react";

import type { AppLocale } from "@/i18n/config";
import OverlayWrapper from "@/components/ui/OverLayWrapper";
import { CustomModal } from "@/components/ui/CustomModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import LangButton from "@/components/ui/LangButton";
import { FarbinLogoSVG } from "@/components/ui/SVG";
import { useLocaleState } from "@/hooks/useLocaleState";
import { usePathname, useRouter } from "@/i18n/routing";
import { shouldShowIOSInstallPrompt } from "@/utils/iosPwa";

const DISMISSED_COOKIE = "farbin_ios_install_prompt_dismissed";
const COOKIE_LIFETIME_DAYS = 3650;

const copy = {
  en: {
    title: "Add the Farbin web app to your phone's Home Screen",
    close: "Close install guide",
    steps: [
      {
        label: "Share",
        description: "Tap this icon in Safari's bottom toolbar",
      },
      {
        label: "Add to Home Screen",
        description: "Choose this option from the menu",
      },
      {
        label: "Add",
        description: "Tap Add to finish installing the app",
      },
    ],
  },
  fa: {
    title: "وب‌اپلیکیشن فربین را به صفحه اصلی تلفن همراه خود اضافه کنید",
    close: "بستن راهنمای نصب",
    steps: [
      {
        label: "Share",
        description: "انتخاب این گزینه از نوار پایین",
      },
      {
        label: "Add to Home Screen",
        description: "انتخاب این گزینه از منوی ظاهر شده",
      },
      {
        label: "Add",
        description: "زدن این گزینه",
      },
    ],
  },
} as const;

function ShareIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 32 32"
      className="h-6 w-6"
      fill="none"
      stroke="var(--color-pinkCustom)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M16 20V3" />
      <path d="m10 9 6-6 6 6" />
      <path d="M10.5 13H8a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3h-2.5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 32 32"
      className="h-6 w-6"
      fill="none"
      stroke="var(--color-pinkCustom)"
      strokeLinecap="round"
      strokeWidth="2.2"
    >
      <path d="M16 5v22M5 16h22" />
    </svg>
  );
}

function IconCircle({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-grayCustom flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
      {children}
    </div>
  );
}

export default function IOSInstallPrompt({ locale }: { locale: AppLocale }) {
  const [open, setOpen] = useState(false);
  const isTabletOrLarger = useMediaQuery("(min-width: 768px)");
  const { currentLang, setCurrentLang } = useLocaleState(locale);
  const router = useRouter();
  const pathname = usePathname();
  const content = copy[currentLang];

  const changeLocale = useCallback(
    (nextLocale: AppLocale) => {
      router.replace(pathname, { locale: nextLocale });
    },
    [pathname, router],
  );

  useEffect(() => {
    const standaloneDisplayMode = window.matchMedia(
      "(display-mode: standalone)",
    ).matches;

    setOpen(
      shouldShowIOSInstallPrompt({
        navigatorLike: navigator,
        standaloneDisplayMode,
        wasDismissed: Cookies.get(DISMISSED_COOKIE) === "1",
      }),
    );
  }, []);

  const dismiss = useCallback(() => {
    Cookies.set(DISMISSED_COOKIE, "1", {
      expires: COOKIE_LIFETIME_DAYS,
      path: "/",
      sameSite: "lax",
      secure: window.location.protocol === "https:",
    });
    setOpen(false);
  }, []);

  const icons = [
    <ShareIcon key="share" />,
    <PlusIcon key="plus" />,
    <FarbinLogoSVG
      key="farbin"
      className="h-6 w-6"
      svgColor="pinkCustom"
      accentColor="slateCustom"
    />,
  ];

  const instructions = (
    <section
      dir={currentLang === "fa" ? "rtl" : "ltr"}
      className="mx-auto flex w-full max-w-sm md:max-w-none flex-col px-6 pt-8 pb-8 sm:px-10"
    >
      <div className="flex items-center justify-between" dir="ltr">
        <button
          dir="ltr"
          type="button"
          onClick={dismiss}
          aria-label={content.close}
          className="bg-grayCustom text-slateCustom hover:text-pinkCustom focus-visible:ring-pinkCustom flex h-10 w-10 items-center justify-center rounded-full transition focus-visible:ring-2 focus-visible:outline-none"
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <LangButton
          currentLang={currentLang}
          setCurrentLang={setCurrentLang}
          onLocaleChange={changeLocale}
          bgColor="grayCustom"
        />
      </div>

      <div className="flex flex-col items-center text-center">
        <div className="border-slateCustom rounded-full border p-4">
          <FarbinLogoSVG
            className="h-14 w-14"
            svgColor="pinkCustom"
            accentColor="slateCustom"
          />
        </div>
        <h2 className="text-slateCustom mt-6 max-w-xs leading-9 font-medium sm:text-2xl sm:leading-10">
          {content.title}
        </h2>
      </div>

      <ol className="mt-10 flex flex-col gap-7 sm:mt-12 sm:gap-9">
        {content.steps.map((step, index) => (
          <li key={step.label} className="flex items-center gap-5 sm:gap-7">
            <IconCircle>{icons[index]}</IconCircle>
            <div className="min-w-0 flex-1">
              <div
                dir="ltr"
                className={`text-slateCustom font-medium ${
                  currentLang === "fa" ? "text-right" : "text-left"
                }`}
              >
                {step.label}
              </div>
              <p className="mt-1 text-sm leading-7 text-gray-500 sm:text-lg sm:leading-8">
                {step.description}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );

  if (isTabletOrLarger) {
    return (
      <CustomModal
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) dismiss();
        }}
        maxWidth="max-w-xl"
        className="bg-grayCustom!"
      >
        {instructions}
      </CustomModal>
    );
  }

  return (
    <AnimatePresence>
      {open ? (
        <OverlayWrapper
          onClose={dismiss}
          snapPoints={["78vh"]}
          initialSnap={1}
          dismissOnOverlay
          aria-label={content.title}
          className="bg-white"
        >
          {instructions}
        </OverlayWrapper>
      ) : null}
    </AnimatePresence>
  );
}
